import React, { useState, useEffect } from 'react';
import { useGameStore } from '../../store/gameStore';

interface Props {
  send: (action: string, data?: object) => void;
}

export function LandingScreen({ send }: Props) {
  const { playerName, setPlayerName, isConnected, availableGames } = useGameStore();
  const [mode, setMode] = useState<'menu' | 'browse'>('menu');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Request game list when entering browse mode
  useEffect(() => {
    if (mode === 'browse' && isConnected) {
      send('listGames');
    }
  }, [mode, isConnected, send]);

  // Auto-refresh game list every 5 seconds while browsing
  useEffect(() => {
    if (mode !== 'browse' || !isConnected) return;
    
    const interval = setInterval(() => {
      send('listGames');
    }, 5000);
    
    return () => clearInterval(interval);
  }, [mode, isConnected, send]);

  const handleCreateGame = () => {
    if (!playerName.trim()) {
      alert('Please enter your name first!');
      return;
    }
    send('createGame', { playerName: playerName.trim() });
  };

  const handleJoinGame = (gameId: string) => {
    if (!playerName.trim()) {
      alert('Please enter your name first!');
      return;
    }
    send('joinGame', { 
      playerName: playerName.trim(), 
      gameId 
    });
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    send('listGames');
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const formatTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className="screen">
      {/* Logo Banner */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <img 
          src="/images/ui/ltc-banner.png"
          alt="Little Toasty Crumb"
          style={{
            maxWidth: '100%',
            width: 360,
            height: 'auto',
            imageRendering: 'auto',
            filter: 'drop-shadow(4px 4px 8px rgba(44, 24, 16, 0.3))',
            marginBottom: 12,
          }}
        />
        <p className="subtitle" style={{ marginTop: 8 }}>
          A cozy multiplayer restaurant game
        </p>
      </div>

      {/* Name Input */}
      <div style={{ marginBottom: 24, width: '100%', maxWidth: 320 }}>
        <label 
          style={{ 
            display: 'block', 
            marginBottom: 8,
            fontWeight: 600,
            color: 'var(--ltc-text-secondary)',
          }}
        >
          Your Name
        </label>
        <input
          type="text"
          className="input"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          placeholder="Enter your name..."
          maxLength={20}
          style={{ textAlign: 'center' }}
        />
      </div>

      {/* Menu or Game Browser */}
      {mode === 'menu' ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 16,
          width: '100%',
          maxWidth: 320,
        }}>
          <button
            className="btn btn-primary btn-large"
            onClick={handleCreateGame}
            disabled={!isConnected}
          >
            🥐 Create Game
          </button>
          <button
            className="btn btn-secondary btn-large"
            onClick={() => setMode('browse')}
            disabled={!isConnected}
          >
            🎮 Browse Games
          </button>
        </div>
      ) : (
        <div style={{ 
          width: '100%', 
          maxWidth: 480,
        }}>
          {/* Header with refresh */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 16,
          }}>
            <h3 style={{ margin: 0, color: 'var(--ltc-primary)' }}>
              🎮 Available Games
            </h3>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{
                background: 'var(--ltc-bg-secondary)',
                border: 'var(--ltc-border)',
                padding: '8px 16px',
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                fontSize: 14,
              }}
            >
              {isRefreshing ? '⏳' : '🔄'} Refresh
            </button>
          </div>

          {/* Game List */}
          <div style={{
            background: 'var(--ltc-bg-card)',
            border: 'var(--ltc-border)',
            maxHeight: 320,
            overflowY: 'auto',
            marginBottom: 16,
          }}>
            {availableGames.length === 0 ? (
              <div style={{ 
                padding: 40, 
                textAlign: 'center',
                color: 'var(--ltc-text-muted)',
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🍃</div>
                <p style={{ margin: 0 }}>No games available right now</p>
                <p style={{ margin: '8px 0 0 0', fontSize: 13 }}>
                  Create your own game to get started!
                </p>
              </div>
            ) : (
              availableGames.map((game, index) => (
                <div
                  key={game.gameId}
                  style={{
                    padding: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    borderBottom: index < availableGames.length - 1 ? 'var(--ltc-border)' : 'none',
                    background: index % 2 === 0 ? 'transparent' : 'var(--ltc-bg-secondary)',
                  }}
                >
                  {/* Host avatar */}
                  <div style={{
                    width: 48,
                    height: 48,
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    border: '3px solid #8b4513',
                    flexShrink: 0,
                  }}>
                    👨‍🍳
                  </div>
                  
                  {/* Game info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 700, 
                      color: 'var(--ltc-text-primary)',
                      marginBottom: 4,
                    }}>
                      {game.hostName}'s Kitchen
                    </div>
                    <div style={{ 
                      fontSize: 13, 
                      color: 'var(--ltc-text-secondary)',
                      display: 'flex',
                      gap: 12,
                    }}>
                      <span>👥 {game.playerCount}/{game.maxPlayers}</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {game.difficulty === 'easy' ? '🟢' : game.difficulty === 'hard' ? '🔴' : '🟡'} {game.difficulty}
                      </span>
                      <span style={{ color: 'var(--ltc-text-muted)' }}>
                        {formatTimeAgo(game.createdAt)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Join button */}
                  <button
                    onClick={() => handleJoinGame(game.gameId)}
                    disabled={game.playerCount >= game.maxPlayers}
                    style={{
                      background: game.playerCount >= game.maxPlayers ? '#ccc' : 'var(--ltc-accent)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      fontWeight: 600,
                      cursor: game.playerCount >= game.maxPlayers ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                    }}
                  >
                    {game.playerCount >= game.maxPlayers ? 'FULL' : 'JOIN'}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Back button and create option */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              className="btn btn-secondary"
              onClick={() => setMode('menu')}
              style={{ flex: 1 }}
            >
              ← Back
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateGame}
              disabled={!isConnected}
              style={{ flex: 1 }}
            >
              🥐 Create New
            </button>
          </div>
          
          <p style={{
            marginTop: 16,
            textAlign: 'center',
            color: 'var(--ltc-text-muted)',
            fontSize: 12,
          }}>
            Games auto-refresh every 5 seconds
          </p>
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        position: 'fixed', 
        bottom: 16, 
        textAlign: 'center',
        color: 'var(--ltc-text-muted)',
        fontSize: 12,
      }}>
        Made with 🍰 and ☕
      </div>
    </div>
  );
}
